import type { PullResult } from '@gachakit/core/types/pack';
import { animateReveal } from '../animations/reveal';
import { triggerRarityFeedback } from '../animations/rarity-effects';

type RevealPhase = 'sealed' | 'opening' | 'stack' | 'summary';

export interface PackPresentation {
  brand?: string;
  name: string;
  label: string;
  artUrl: string;
  primary: string;
  secondary: string;
}

const styles = `
  :host {
    position: fixed;
    inset: 0;
    display: none;
    place-items: stretch;
    padding:
      calc(12px + env(safe-area-inset-top))
      12px
      calc(12px + env(safe-area-inset-bottom));
    background:
      linear-gradient(180deg, rgba(8, 9, 8, 0.98), rgba(8, 9, 8, 0.94)),
      repeating-linear-gradient(90deg, rgba(255,255,255,0.025) 0 1px, transparent 1px 17px);
    backdrop-filter: blur(22px);
    z-index: 30;
  }

  :host([open]) {
    display: grid;
  }

  .wrap {
    width: min(100%, 460px);
    min-height: 100%;
    margin: 0 auto;
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    gap: 10px;
  }

  .hud {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: center;
  }

  .pill {
    min-height: 34px;
    display: inline-flex;
    align-items: center;
    padding: 0 12px;
    border-radius: 999px;
    color: rgba(247,239,218,0.76);
    background: rgba(247,239,218,0.08);
    border: 1px solid rgba(247,239,218,0.12);
    font: 900 0.72rem/1 var(--font-display, "Sora", sans-serif);
  }

  .stage {
    --rarity-color: #9eaa9b;
    position: relative;
    min-height: 0;
    overflow: hidden;
    border-radius: 8px;
    border: 1px solid color-mix(in oklab, var(--rarity-color) 36%, rgba(255,255,255,0.12));
    background:
      linear-gradient(145deg, color-mix(in oklab, var(--rarity-color) 16%, transparent), transparent 42%),
      linear-gradient(180deg, rgba(247,239,218,0.1), rgba(247,239,218,0.025)),
      #0d100d;
    box-shadow: 0 24px 58px rgba(0,0,0,0.54);
  }

  .stage::before,
  .stage::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  .stage::before {
    background:
      linear-gradient(118deg, transparent 0 34%, rgba(255,255,255,0.12) 48%, transparent 62%),
      repeating-linear-gradient(135deg, rgba(255,255,255,0.035) 0 1px, transparent 1px 12px);
    mix-blend-mode: screen;
    opacity: 0.62;
  }

  .stage::after {
    border-radius: inherit;
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.05);
  }

  .stage[data-rarity='common'] { --rarity-color: var(--rarity-common, #9eaa9b); }
  .stage[data-rarity='uncommon'] { --rarity-color: var(--rarity-uncommon, #58d68d); }
  .stage[data-rarity='rare'] { --rarity-color: var(--rarity-rare, #68c7ff); }
  .stage[data-rarity='epic'] { --rarity-color: var(--rarity-epic, #e16f9d); }
  .stage[data-rarity='legendary'] { --rarity-color: var(--rarity-legendary, #f5c76b); }

  .sealed,
  .opening,
  .stack,
  .summary {
    position: relative;
    z-index: 1;
    min-height: 100%;
  }

  .sealed,
  .opening {
    display: grid;
    place-items: center;
    padding: 18px 14px 24px;
    perspective: 1300px;
  }

  .pack-shell {
    width: 100%;
    display: grid;
    justify-items: center;
    gap: 18px;
  }

  .pack {
    --pack-primary: #80dfff;
    --pack-secondary: #e16f9d;
    --tear: 0;
    --rx: 0deg;
    --ry: 0deg;
    --mx: 50%;
    --my: 26%;
    --tear-x: 0px;
    --tear-shift: 0px;
    --tear-lift: 0px;
    --tear-angle: 0deg;
    --tear-handle-angle: 0deg;
    --tear-pct: 0%;
    --tear-glow: 0.22;
    --tear-shadow: 0;
    position: relative;
    width: min(72vw, 292px);
    aspect-ratio: 0.66;
    transform-style: preserve-3d;
    transform: rotateX(var(--rx)) rotateY(var(--ry)) rotateZ(-1deg);
    transition: transform 140ms ease;
    touch-action: none;
  }

  .pack-body {
    position: absolute;
    inset: 0;
    overflow: hidden;
    border-radius: 16px 16px 22px 22px;
    border: 1px solid rgba(255,255,255,0.34);
    background:
      linear-gradient(135deg, rgba(255,255,255,0.76), transparent 15% 38%, rgba(255,255,255,0.3) 49%, transparent 62%),
      linear-gradient(155deg, var(--pack-primary), #11141d 48%, var(--pack-secondary));
    box-shadow:
      0 34px 54px rgba(0,0,0,0.56),
      0 0 38px color-mix(in oklab, var(--pack-primary) 20%, transparent),
      0 3px 0 rgba(255,255,255,0.18) inset,
      0 0 0 1px rgba(0,0,0,0.42) inset;
  }

  .pack-body::before,
  .pack-body::after,
  .pack-art::before,
  .pack-art::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  .pack-body::before {
    z-index: 8;
    left: -65%;
    width: 74%;
    background: linear-gradient(105deg, transparent 18%, rgba(255,255,255,0.64) 48%, transparent 76%);
    mix-blend-mode: screen;
    transform: translateX(var(--mx));
    opacity: 0.62;
  }

  .pack-body::after {
    z-index: 7;
    background:
      repeating-linear-gradient(112deg, rgba(255,255,255,0.085) 0 1px, transparent 1px 7px),
      radial-gradient(circle at var(--mx) var(--my), rgba(255,255,255,0.3), transparent 34%);
    mix-blend-mode: screen;
  }

  .pack-art {
    position: absolute;
    z-index: 1;
    inset: 22px 0 19px;
    background-position: center;
    background-size: cover;
    filter: saturate(1.04) contrast(1.06);
  }

  .pack-art::before {
    background:
      linear-gradient(180deg, rgba(7,9,16,0.1), transparent 36%, rgba(7,9,16,0.92)),
      linear-gradient(135deg, color-mix(in oklab, var(--pack-primary) 46%, transparent), transparent 42% 64%, color-mix(in oklab, var(--pack-secondary) 52%, transparent));
  }

  .pack-art::after {
    background: radial-gradient(circle at 50% 34%, transparent 0 22%, rgba(4,6,12,0.2) 62%, rgba(4,6,12,0.68));
  }

  .pack-copy {
    position: absolute;
    z-index: 10;
    left: 14px;
    right: 14px;
    bottom: 34px;
    display: grid;
    gap: 6px;
    padding: 14px;
    border: 1px solid rgba(255,255,255,0.3);
    border-radius: 11px;
    background:
      linear-gradient(135deg, color-mix(in oklab, var(--pack-primary) 72%, #10141d), color-mix(in oklab, var(--pack-secondary) 62%, #090b12));
    box-shadow: 0 14px 30px rgba(0,0,0,0.38), inset 0 1px rgba(255,255,255,0.24);
    transform: translateZ(16px);
  }

  .pack-copy small,
  .pack-copy strong,
  .pack-copy span {
    display: block;
  }

  .pack-copy small {
    color: rgba(255,255,255,0.78);
    font: 900 0.61rem/1 var(--font-display, "Sora", sans-serif);
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  .pack-copy strong {
    color: #fffdf5;
    font: 900 1.42rem/0.94 var(--font-display, "Sora", sans-serif);
    text-transform: uppercase;
    text-wrap: balance;
  }

  .pack-copy span {
    color: rgba(255,255,255,0.76);
    font-size: 0.7rem;
  }

  .bottom-crimp,
  .pack-top {
    position: absolute;
    left: 0;
    right: 0;
    height: 24px;
    background:
      linear-gradient(180deg, rgba(255,255,255,0.62), transparent 34%, rgba(0,0,0,0.28) 76%, rgba(255,255,255,0.18)),
      repeating-linear-gradient(90deg, rgba(255,255,255,0.38) 0 2px, rgba(0,0,0,0.2) 2px 5px),
      var(--pack-primary);
  }

  .bottom-crimp {
    z-index: 12;
    bottom: 0;
    transform: rotate(180deg);
    box-shadow: 0 -1px rgba(255,255,255,0.32);
  }

  .pack-top {
    z-index: 15;
    top: 0;
    height: 54px;
    overflow: visible;
    clip-path: polygon(0 0, 100% 0, 100% 74%, 97% 84%, 93% 76%, 89% 88%, 84% 78%, 79% 89%, 74% 77%, 69% 86%, 64% 75%, 59% 88%, 54% 78%, 49% 89%, 44% 76%, 39% 87%, 34% 77%, 29% 89%, 24% 76%, 19% 87%, 14% 77%, 9% 88%, 4% 78%, 0 86%);
    transform-origin: 8% 50%;
    transform:
      translate3d(var(--tear-shift), var(--tear-lift), 24px)
      rotate(var(--tear-angle));
    filter: drop-shadow(0 8px 7px rgba(0,0,0,var(--tear-shadow)));
  }

  .pack-top::after {
    content: "";
    position: absolute;
    left: 14px;
    bottom: 4px;
    width: 32px;
    height: 14px;
    border-radius: 50%;
    background: rgba(255,255,255,0.5);
    filter: blur(7px);
    opacity: var(--tear-glow);
  }

  .tear-track {
    position: absolute;
    z-index: 18;
    left: 12px;
    right: 12px;
    top: 37px;
    height: 26px;
    pointer-events: none;
  }

  .tear-track::before {
    content: "";
    position: absolute;
    left: 12px;
    right: 12px;
    top: 11px;
    height: 2px;
    background:
      linear-gradient(90deg, var(--pack-primary) 0 var(--tear-pct), rgba(255,255,255,0.24) var(--tear-pct));
    box-shadow: 0 0 12px color-mix(in oklab, var(--pack-primary) 46%, transparent);
  }

  .tear-handle {
    position: absolute;
    z-index: 22;
    left: 3px;
    top: 28px;
    width: 48px;
    height: 48px;
    display: grid;
    place-items: center;
    border: 1px solid rgba(255,255,255,0.52);
    border-radius: 50%;
    padding: 0;
    color: #07100c;
    background: linear-gradient(135deg, #ffffff, var(--pack-primary));
    box-shadow: 0 10px 24px rgba(0,0,0,0.38), 0 0 22px color-mix(in oklab, var(--pack-primary) 34%, transparent);
    transform: translateX(var(--tear-x)) rotate(var(--tear-handle-angle));
    touch-action: none;
    cursor: grab;
  }

  .tear-handle::before {
    content: "⇢";
    font: 900 1.15rem/1 var(--font-display, "Sora", sans-serif);
  }

  .tear-handle:active {
    cursor: grabbing;
  }

  .pack[data-tearing='true'] .tear-handle {
    transform: translateX(var(--tear-x)) scale(1.08) rotate(var(--tear-handle-angle));
  }

  .pack[data-resetting='true'] .pack-top,
  .pack[data-resetting='true'] .tear-handle {
    transition: transform 220ms ease, filter 220ms ease;
  }

  .hint {
    max-width: 30ch;
    text-align: center;
    color: rgba(247,239,218,0.72);
    font-size: 0.9rem;
    line-height: 1.35;
  }

  .opening .pack-top {
    animation: foil-top-fly 780ms cubic-bezier(0.18, 0.9, 0.22, 1) forwards;
  }

  .opening .pack-body {
    animation: opened-pack-drop 780ms cubic-bezier(0.22, 0.8, 0.22, 1) forwards;
  }

  .opening .tear-handle,
  .opening .tear-track {
    display: none;
  }

  .card-peek {
    position: absolute;
    z-index: 0;
    left: 18%;
    right: 18%;
    top: 8px;
    height: 72%;
    border-radius: 14px;
    background:
      linear-gradient(135deg, rgba(255,255,255,0.42), transparent 24%),
      linear-gradient(145deg, var(--pack-primary), var(--pack-secondary));
    box-shadow: 0 20px 36px rgba(0,0,0,0.42);
    animation: card-peek 780ms cubic-bezier(0.18, 0.9, 0.22, 1) forwards;
  }

  .flash {
    position: absolute;
    inset: 0;
    background:
      linear-gradient(115deg, transparent 0 30%, color-mix(in oklab, var(--rarity-color) 48%, #ffffff) 48%, transparent 68%);
    opacity: 0;
    animation: reveal-flash 700ms ease-out forwards;
  }

  .stack {
    display: grid;
    grid-template-rows: auto minmax(0, 1fr) auto;
    gap: 10px;
    padding: 14px;
  }

  .stack-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .stack-title {
    min-height: 36px;
    display: inline-flex;
    align-items: center;
    padding: 0 12px;
    border-radius: 999px;
    color: #07100c;
    background: linear-gradient(135deg, color-mix(in oklab, var(--rarity-color) 82%, #ffffff), var(--rarity-color));
    font: 900 0.78rem/1 var(--font-display, "Sora", sans-serif);
    text-transform: uppercase;
  }

  .counter {
    color: rgba(247,239,218,0.74);
    font: 900 0.82rem/1 var(--font-display, "Sora", sans-serif);
  }

  .stack-board {
    position: relative;
    min-height: 0;
    display: grid;
    place-items: center;
    perspective: 1200px;
  }

  .rear-card,
  .front-slot,
  .card-back {
    position: absolute;
    width: min(72vw, 248px);
    max-width: 248px;
    aspect-ratio: 0.72;
    border-radius: 16px;
  }

  .rear-card {
    overflow: hidden;
    border: 1px solid color-mix(in oklab, var(--rarity-color) 42%, rgba(255,255,255,0.14));
    background:
      linear-gradient(145deg, color-mix(in oklab, var(--rarity-color) 18%, transparent), rgba(255,255,255,0.05)),
      #11140f;
    box-shadow: 0 16px 34px rgba(0,0,0,0.32);
    background-size: cover;
    background-position: center;
  }

  .rear-card::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, rgba(0,0,0,0.12), rgba(0,0,0,0.54)),
      linear-gradient(125deg, transparent 0 38%, rgba(255,255,255,0.12) 50%, transparent 62%);
  }

  .rear-card[data-order='1'] { transform: translate3d(48px, 8px, -40px) rotate(8deg); }
  .rear-card[data-order='2'] { transform: translate3d(30px, 16px, -80px) rotate(5deg); }
  .rear-card[data-order='3'] { transform: translate3d(12px, 24px, -120px) rotate(1deg); }
  .rear-card[data-order='4'] { transform: translate3d(-6px, 30px, -160px) rotate(-3deg); }

  .front-slot {
    display: grid;
    place-items: center;
    transform-style: preserve-3d;
  }

  .front-slot.collecting {
    pointer-events: none;
  }

  .card-back {
    display: grid;
    place-items: center;
    border: 1px solid rgba(247,239,218,0.14);
    background:
      linear-gradient(145deg, color-mix(in oklab, var(--rarity-color) 22%, transparent), rgba(255,255,255,0.05)),
      repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 12px),
      #11140f;
    color: rgba(247,239,218,0.72);
    font: 900 0.9rem/1 var(--font-display, "Sora", sans-serif);
    text-transform: uppercase;
  }

  gacha-card {
    width: min(72vw, 248px);
    max-width: 248px;
  }

  .stack-actions {
    display: grid;
    gap: 7px;
    justify-items: center;
  }

  .binder-target {
    min-height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    padding: 0 12px;
    border-radius: 999px;
    border: 1px solid rgba(247,239,218,0.12);
    background: rgba(247,239,218,0.07);
    color: rgba(247,239,218,0.72);
    font-size: 0.75rem;
    text-align: center;
  }

  .binder-dot {
    width: 13px;
    height: 17px;
    border-radius: 3px;
    background: linear-gradient(135deg, #a7f5ce, #f5c76b);
    transform: rotate(-8deg);
    box-shadow: 0 0 18px rgba(167,245,206,0.34);
  }

  .tap,
  .close {
    width: min(100%, 360px);
    min-height: 46px;
    border: 0;
    border-radius: 8px;
    color: #07100c;
    background: linear-gradient(135deg, #a7f5ce, #f5c76b);
    font: 900 0.88rem/1 var(--font-display, "Sora", sans-serif);
    text-transform: uppercase;
  }

  .tap:active,
  .close:active {
    transform: translateY(1px) scale(0.99);
  }

  .summary {
    display: grid;
    align-content: center;
    gap: 14px;
    padding: 18px;
  }

  .summary-card {
    padding: 16px;
    border-radius: 8px;
    border: 1px solid rgba(247,239,218,0.12);
    background:
      linear-gradient(145deg, color-mix(in oklab, var(--rarity-color) 18%, transparent), rgba(247,239,218,0.05)),
      rgba(8, 9, 8, 0.44);
  }

  .summary-card h3 {
    margin: 0;
    color: #fff8e8;
    font: 900 1.26rem/1 var(--font-display, "Sora", sans-serif);
  }

  .summary-card p {
    margin: 8px 0 0;
    color: rgba(247,239,218,0.72);
    line-height: 1.35;
    font-size: 0.9rem;
  }

  .summary-metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .summary-metrics span {
    min-width: 0;
    padding: 13px 8px;
    border-radius: 8px;
    background: rgba(247,239,218,0.06);
    border: 1px solid rgba(247,239,218,0.1);
    text-align: center;
  }

  .summary-metrics strong,
  .summary-metrics small {
    display: block;
  }

  .summary-metrics strong {
    color: #fff8e8;
    font: 900 1.1rem/1 var(--font-display, "Sora", sans-serif);
  }

  .summary-metrics small {
    margin-top: 7px;
    color: rgba(247,239,218,0.65);
    font-size: 0.7rem;
  }

  @keyframes foil-top-fly {
    from {
      opacity: 1;
      transform: translate3d(22px, -5px, 24px) rotate(-5deg);
    }

    to {
      opacity: 0;
      transform: translate3d(190px, -94px, 80px) rotate(34deg);
    }
  }

  @keyframes opened-pack-drop {
    0%, 38% {
      opacity: 1;
      transform: translateY(0) rotate(-1deg);
    }

    100% {
      opacity: 0;
      transform: translateY(54px) rotate(4deg) scale(0.94);
    }
  }

  @keyframes card-peek {
    0% {
      opacity: 0;
      transform: translateY(42px) scale(0.88);
    }

    34% {
      opacity: 1;
    }

    100% {
      opacity: 0;
      transform: translateY(-92px) scale(1.04);
    }
  }

  @keyframes reveal-flash {
    0% { opacity: 0; transform: translateX(-24%); }
    26% { opacity: 0.95; }
    100% { opacity: 0; transform: translateX(24%); }
  }

  @media (max-width: 374px) {
    :host {
      padding-inline: 8px;
    }

    .rear-card,
    .front-slot,
    .card-back,
    gacha-card {
      width: min(68vw, 226px);
    }

    .binder-target {
      font-size: 0.7rem;
    }
  }

  @media (max-height: 700px) {
    :host {
      padding-block:
        calc(8px + env(safe-area-inset-top))
        calc(8px + env(safe-area-inset-bottom));
    }

    .wrap,
    .stack {
      gap: 8px;
    }

    .stack {
      padding: 12px;
    }

    .rear-card,
    .front-slot,
    .card-back,
    gacha-card {
      width: min(68vw, 226px);
    }

    .binder-target {
      min-height: 34px;
      font-size: 0.7rem;
    }

    .tap,
    .close {
      min-height: 42px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .opening .pack-top,
    .opening .pack-body,
    .card-peek,
    .flash {
      animation: none;
    }
  }
`;

export class PullResultElement extends HTMLElement {
  private readonly root: ShadowRoot;
  private currentPack: PullResult[] = [];
  private currentIndex = 0;
  private phase: RevealPhase = 'sealed';
  private loadedImages = new Set<number>();
  private preloadToken = 0;
  private openingTimeout: number | null = null;
  private collecting = false;
  private feedbackIndex = -1;
  private tearProgress = 0;
  private tearPointerId: number | null = null;
  private tearStartX = 0;
  private tearDistance = 1;
  private packPresentation: PackPresentation = {
    brand: 'Species Gacha',
    name: 'Field Pack',
    label: 'Five-card field booster',
    artUrl: '/pack/sotw.webp',
    primary: '#80dfff',
    secondary: '#e16f9d'
  };

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this.render();
  }

  disconnectedCallback(): void {
    this.clearOpeningTimeout();
  }

  set presentation(value: PackPresentation) {
    this.packPresentation = { ...value };
    if (this.currentPack.length > 0) {
      this.render();
    }
  }

  get presentation(): PackPresentation {
    return { ...this.packPresentation };
  }

  set pack(value: PullResult[] | null) {
    this.clearOpeningTimeout();
    this.currentPack = value ? [...value] : [];
    this.currentIndex = 0;
    this.phase = 'sealed';
    this.collecting = false;
    this.feedbackIndex = -1;
    this.tearProgress = 0;
    this.tearPointerId = null;
    this.loadedImages = new Set<number>();
    this.preloadToken += 1;
    this.toggleAttribute('open', this.currentPack.length > 0);

    if (this.currentPack.length > 0) {
      void this.preloadPack(this.preloadToken);
    }

    this.render();
  }

  get pack(): PullResult[] | null {
    return this.currentPack.length > 0 ? this.currentPack : null;
  }

  private render(): void {
    if (this.currentPack.length === 0) {
      this.root.innerHTML = '';
      return;
    }

    const current = this.currentPack[this.currentIndex] ?? this.currentPack[this.currentPack.length - 1];
    const displayIndex = Math.min(this.currentIndex + 1, this.currentPack.length);
    this.root.innerHTML = `
      <style>${styles}</style>
      <div class="wrap">
        <div class="hud">
          <span class="pill">${phaseLabel(this.phase)}</span>
          <span class="pill">${displayIndex}/${this.currentPack.length}</span>
        </div>
        <div class="stage" data-rarity="${current.card.rarity}">
          ${this.renderPhase()}
        </div>
      </div>
    `;

    if (this.phase === 'sealed') {
      this.bindPackInteractions();
    }

    if (this.phase === 'stack') {
      this.hydrateFrontCard();
      this.root.querySelector('.front-slot')?.addEventListener('click', () => this.collectCurrentCard(), { once: true });
      this.root.querySelector('.tap')?.addEventListener('click', () => this.collectCurrentCard(), { once: true });
    }

    if (this.phase === 'summary') {
      this.root.querySelector('.close')?.addEventListener('click', () => this.close(), { once: true });
    }
  }

  private renderPhase(): string {
    switch (this.phase) {
      case 'sealed':
        return this.renderSealed();
      case 'opening':
        return this.renderOpening();
      case 'stack':
        return this.renderStack();
      case 'summary':
        return this.renderSummary();
    }
  }

  private renderSealed(): string {
    const firstReady = this.loadedImages.has(0);

    return `
      <div class="sealed">
        <div class="pack-shell">
          ${this.renderFoilPack(false, firstReady)}
          <div class="hint">${firstReady ? 'Grab the tab and drag it all the way across.' : 'Preloading the first card under the foil…'}</div>
        </div>
      </div>
    `;
  }

  private renderOpening(): string {
    return `
      <div class="opening">
        <div class="flash"></div>
        <div class="pack-shell">
          ${this.renderFoilPack(true, true)}
          <div class="hint">Wrapper split. The stack is coming out.</div>
        </div>
      </div>
    `;
  }

  private renderFoilPack(opening: boolean, ready: boolean): string {
    const presentation = this.packPresentation;
    const style = [
      `--pack-primary:${presentation.primary}`,
      `--pack-secondary:${presentation.secondary}`,
      `--tear:${opening ? 1 : this.tearProgress}`,
      `--tear-x:${opening ? 236 : 0}px`,
      `--tear-shift:${opening ? 22 : 0}px`,
      `--tear-lift:${opening ? -5 : 0}px`,
      `--tear-angle:${opening ? -5 : 0}deg`,
      `--tear-handle-angle:${opening ? 24 : 0}deg`,
      `--tear-pct:${opening ? 100 : 0}%`,
      `--tear-glow:${opening ? 0.86 : 0.22}`,
      `--tear-shadow:${opening ? 0.34 : 0}`
    ].join(';');

    return `
      <div class="pack" data-ready="${ready}" style="${style}" aria-label="${escapeAttribute(presentation.name)} sealed foil pack">
        ${opening ? '<span class="card-peek" aria-hidden="true"></span>' : ''}
        <div class="pack-body">
          <span class="pack-art" style="background-image:url('${escapeStyleUrl(presentation.artUrl)}')"></span>
          <span class="pack-copy">
            <small>${escapeHtml(presentation.brand ?? 'GachaKit')} · 5 cards</small>
            <strong>${escapeHtml(presentation.name)}</strong>
            <span>${escapeHtml(presentation.label)}</span>
          </span>
          <span class="bottom-crimp" aria-hidden="true"></span>
        </div>
        <span class="pack-top" aria-hidden="true"></span>
        ${
          opening
            ? ''
            : `
              <span class="tear-track" aria-hidden="true"></span>
              <button class="tear-handle" type="button" aria-label="Drag right to tear open ${escapeAttribute(presentation.name)}" ${ready ? '' : 'disabled'}></button>
            `
        }
      </div>
    `;
  }

  private renderStack(): string {
    const current = this.currentPack[this.currentIndex];
    const nextCards = this.currentPack.slice(this.currentIndex + 1, this.currentIndex + 5);
    const rearCards = nextCards
      .map((result, index) => {
        const packIndex = this.currentIndex + index + 1;
        const loaded = this.loadedImages.has(packIndex);
        const background = loaded ? `style="background-image:url('${escapeAttribute(result.card.imageUrl ?? '')}')"` : '';
        return `
          <div class="rear-card" data-order="${index + 1}" data-loaded="${loaded}" data-rarity="${result.card.rarity}" ${background}></div>
        `;
      })
      .reverse()
      .join('');

    return `
      <div class="stack">
        <div class="stack-top">
          <div class="stack-title">${current.card.rarity}</div>
          <span class="counter">${this.currentIndex + 1}/${this.currentPack.length}</span>
        </div>
        <div class="stack-board">
          ${rearCards}
          <div class="front-slot ${this.collecting ? 'collecting' : ''}">
            ${
              this.loadedImages.has(this.currentIndex)
                ? '<gacha-card compact></gacha-card>'
                : '<div class="card-back">Loading</div>'
            }
          </div>
        </div>
        <div class="stack-actions">
          <div class="binder-target">
            <span class="binder-dot" aria-hidden="true"></span>
            <span>Tap the front card to file it in the binder</span>
          </div>
          <button class="tap" type="button">Collect Card</button>
        </div>
      </div>
    `;
  }

  private renderSummary(): string {
    const newCards = this.currentPack.filter((result) => result.isNew).length;
    const rareCards = this.currentPack.filter((result) =>
      ['rare', 'epic', 'legendary'].includes(result.card.rarity)
    ).length;

    return `
      <div class="summary">
        <div class="summary-card">
          <h3>Filed to binder</h3>
          <p>All five cards are saved locally. The rarest pull defines this pack's holo treatment.</p>
        </div>
        <div class="summary-metrics">
          <span><strong>${this.currentPack.length}</strong><small>Cards</small></span>
          <span><strong>${newCards}</strong><small>New</small></span>
          <span><strong>${rareCards}</strong><small>Rare+</small></span>
        </div>
        <button class="close" type="button">Return to Vault</button>
      </div>
    `;
  }

  private async preloadPack(token: number): Promise<void> {
    const firstCard = this.currentPack[0]?.card;
    if (firstCard) {
      await preloadImage(firstCard.imageUrl);
      if (token !== this.preloadToken) {
        return;
      }
      this.loadedImages.add(0);
      this.render();
    }

    this.currentPack.slice(1).forEach((result, offset) => {
      const packIndex = offset + 1;
      void preloadImage(result.card.imageUrl).then(() => {
        if (token !== this.preloadToken) {
          return;
        }
        this.loadedImages.add(packIndex);
        if (this.phase === 'stack') {
          this.render();
        }
      });
    });
  }

  private hydrateFrontCard(): void {
    const current = this.currentPack[this.currentIndex];
    const cardElement = this.root.querySelector('gacha-card') as (HTMLElement & {
      card: PullResult['card'];
    }) | null;

    if (!cardElement || !current) {
      return;
    }

    cardElement.setAttribute('compact', '');
    cardElement.setAttribute('data-status', current.isNew ? 'new file' : 'duplicate');
    cardElement.card = current.card;
    animateReveal(cardElement);

    if (this.feedbackIndex !== this.currentIndex) {
      this.feedbackIndex = this.currentIndex;
      triggerRarityFeedback(current.card.rarity);
    }
  }

  private bindPackInteractions(): void {
    const pack = this.root.querySelector<HTMLElement>('.pack');
    const handle = this.root.querySelector<HTMLButtonElement>('.tear-handle');
    if (!pack || !handle || !this.loadedImages.has(0)) {
      return;
    }

    handle.addEventListener('pointerdown', (event) => this.startTear(event));
    handle.addEventListener('click', () => this.completeTear());
    pack.addEventListener('pointermove', (event) => this.updatePackTilt(pack, event));
    pack.addEventListener('pointerleave', () => this.resetPackTilt(pack));
  }

  private startTear(event: PointerEvent): void {
    if (this.phase !== 'sealed' || !this.loadedImages.has(0)) {
      return;
    }

    const handle = event.currentTarget as HTMLButtonElement;
    const pack = this.root.querySelector<HTMLElement>('.pack');
    if (!pack) {
      return;
    }

    event.preventDefault();
    this.tearPointerId = event.pointerId;
    this.tearStartX = event.clientX;
    this.tearDistance = Math.max(1, pack.clientWidth - handle.clientWidth - 8);
    pack.dataset.tearing = 'true';
    delete pack.dataset.resetting;
    handle.setPointerCapture(event.pointerId);
    handle.addEventListener('pointermove', this.handleTearMove);
    handle.addEventListener('pointerup', this.handleTearEnd);
    handle.addEventListener('pointercancel', this.handleTearEnd);
  }

  private readonly handleTearMove = (event: PointerEvent): void => {
    if (event.pointerId !== this.tearPointerId || this.phase !== 'sealed') {
      return;
    }

    event.preventDefault();
    const progress = Math.max(0, Math.min(1, (event.clientX - this.tearStartX) / this.tearDistance));
    this.tearProgress = progress;
    this.updateTearVisual(progress);

    if (progress >= 0.84) {
      this.completeTear();
    }
  };

  private readonly handleTearEnd = (event: PointerEvent): void => {
    if (event.pointerId !== this.tearPointerId) {
      return;
    }

    const handle = event.currentTarget as HTMLButtonElement;
    this.releaseTearHandle(handle, event.pointerId);
    if (this.tearProgress >= 0.7) {
      this.completeTear();
      return;
    }

    const pack = this.root.querySelector<HTMLElement>('.pack');
    if (pack) {
      pack.dataset.resetting = 'true';
      delete pack.dataset.tearing;
    }
    this.tearProgress = 0;
    this.updateTearVisual(0);
    window.setTimeout(() => {
      const currentPack = this.root.querySelector<HTMLElement>('.pack');
      if (currentPack) {
        delete currentPack.dataset.resetting;
      }
    }, 240);
  };

  private releaseTearHandle(handle: HTMLButtonElement, pointerId: number): void {
    if (handle.hasPointerCapture(pointerId)) {
      handle.releasePointerCapture(pointerId);
    }
    handle.removeEventListener('pointermove', this.handleTearMove);
    handle.removeEventListener('pointerup', this.handleTearEnd);
    handle.removeEventListener('pointercancel', this.handleTearEnd);
    this.tearPointerId = null;
  }

  private updateTearVisual(progress: number): void {
    const pack = this.root.querySelector<HTMLElement>('.pack');
    if (!pack) {
      return;
    }

    pack.style.setProperty('--tear', progress.toFixed(3));
    pack.style.setProperty('--tear-x', `${progress * this.tearDistance}px`);
    pack.style.setProperty('--tear-shift', `${progress * 22}px`);
    pack.style.setProperty('--tear-lift', `${progress * -5}px`);
    pack.style.setProperty('--tear-angle', `${progress * -5}deg`);
    pack.style.setProperty('--tear-handle-angle', `${progress * 24}deg`);
    pack.style.setProperty('--tear-pct', `${progress * 100}%`);
    pack.style.setProperty('--tear-glow', String(0.22 + progress * 0.64));
    pack.style.setProperty('--tear-shadow', String(progress * 0.34));
  }

  private updatePackTilt(pack: HTMLElement, event: PointerEvent): void {
    if (this.tearPointerId !== null || event.pointerType === 'touch') {
      return;
    }
    const rect = pack.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    pack.style.setProperty('--ry', `${(x - 0.5) * 9}deg`);
    pack.style.setProperty('--rx', `${(0.5 - y) * 7}deg`);
    pack.style.setProperty('--mx', `${x * 100}%`);
    pack.style.setProperty('--my', `${y * 100}%`);
  }

  private resetPackTilt(pack: HTMLElement): void {
    if (this.tearPointerId !== null) {
      return;
    }
    pack.style.setProperty('--ry', '0deg');
    pack.style.setProperty('--rx', '0deg');
    pack.style.setProperty('--mx', '50%');
    pack.style.setProperty('--my', '26%');
  }

  private completeTear(): void {
    if (this.phase !== 'sealed' || !this.loadedImages.has(0)) {
      return;
    }

    const handle = this.root.querySelector<HTMLButtonElement>('.tear-handle');
    if (handle && this.tearPointerId !== null) {
      this.releaseTearHandle(handle, this.tearPointerId);
    }

    this.tearProgress = 1;
    this.phase = 'opening';
    if ('vibrate' in navigator) {
      navigator.vibrate([18, 28, 34]);
    }
    this.render();

    this.clearOpeningTimeout();
    this.openingTimeout = window.setTimeout(() => {
      this.phase = 'stack';
      this.render();
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 80 : 780);
  }

  private async collectCurrentCard(): Promise<void> {
    if (this.collecting || this.phase !== 'stack') {
      return;
    }

    this.collecting = true;
    const cardSlot = this.root.querySelector('.front-slot') as HTMLElement | null;
    const binder = this.root.querySelector('.binder-target') as HTMLElement | null;

    if (cardSlot && binder && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const cardRect = cardSlot.getBoundingClientRect();
      const binderRect = binder.getBoundingClientRect();
      const translateX = binderRect.left + binderRect.width / 2 - (cardRect.left + cardRect.width / 2);
      const translateY = binderRect.top + binderRect.height / 2 - (cardRect.top + cardRect.height / 2);

      const animation = cardSlot.animate(
        [
          { transform: 'translate3d(0,0,0) scale(1) rotate(0deg)', opacity: 1 },
          {
            transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(0.14) rotate(18deg)`,
            opacity: 0.12
          }
        ],
        {
          duration: 500,
          easing: 'cubic-bezier(0.18, 0.9, 0.22, 1)',
          fill: 'forwards'
        }
      );

      await Promise.race([
        animation.finished.catch(() => undefined),
        new Promise<void>((resolve) => {
          window.setTimeout(resolve, 650);
        })
      ]);
    }

    this.currentIndex += 1;
    this.collecting = false;

    if (this.currentIndex >= this.currentPack.length) {
      this.phase = 'summary';
      this.render();
      return;
    }

    this.phase = 'stack';
    this.render();
  }

  private clearOpeningTimeout(): void {
    if (this.openingTimeout !== null) {
      window.clearTimeout(this.openingTimeout);
      this.openingTimeout = null;
    }
  }

  private close(): void {
    this.pack = null;
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
  }
}

if (!customElements.get('pull-result')) {
  customElements.define('pull-result', PullResultElement);
}

function phaseLabel(phase: RevealPhase): string {
  switch (phase) {
    case 'sealed':
      return 'Rip the foil';
    case 'opening':
      return 'Wrapper split';
    case 'stack':
      return 'Filing cards';
    case 'summary':
      return 'Pack summary';
  }
}

function preloadImage(url: string | undefined): Promise<void> {
  if (!url) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'high';
    image.onload = () => resolve();
    image.onerror = () => resolve();
    image.src = url;
  });
}

function escapeAttribute(value: string): string {
  return value.replaceAll("'", '%27').replaceAll('"', '&quot;');
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}

function escapeStyleUrl(value: string): string {
  return value.replaceAll('\\', '\\\\').replaceAll("'", "\\'");
}
